import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getInitials } from "@/lib/utils";
import type { SubsSearchResult } from "@/app/vendors/subs/subs.entity";
import { searchSubcontractors } from "@/app/vendors/subs/subs.api";

interface InviteSubsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { type: "existing" | "invite"; organizationId?: string; email?: string }) => Promise<any>;
  saving: boolean;
  error: string | null;
}

export function InviteSubsModal({ open, onOpenChange, onSubmit, saving, error }: InviteSubsModalProps) {
  const [email, setEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [existingOrgs, setExistingOrgs] = useState<SubsSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<SubsSearchResult | null>(null);
  const [mode, setMode] = useState<"search" | "existing" | "invite">("search");

  const searchOrganizations = async (query: string) => {
    if (!query.trim()) {
      setExistingOrgs([]);
      return;
    }

    setLoading(true);
    try {
      setExistingOrgs(await searchSubcontractors(query.trim()));
    } catch (error) {
      console.error("Error searching organizations:", error);
      setExistingOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchOrganizations(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === "existing" && selectedOrg) {
      await onSubmit({ type: "existing", organizationId: selectedOrg.id });
    } else if (mode === "invite" && email.trim()) {
      await onSubmit({ type: "invite", email: email.trim() });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setEmail("");
      setSearchQuery("");
      setSelectedOrg(null);
      setMode("search");
      setExistingOrgs([]);
    }
    onOpenChange(open);
  };

  const handleOrgSelect = (org: SubsSearchResult) => {
    if (org.linked) return;
    setSelectedOrg(org);
    setMode("existing");
  };

  const handleInviteNew = () => {
    setMode("invite");
    setSelectedOrg(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Subs Organization</DialogTitle>
          <DialogDescription>Search for existing subs organizations or invite a new one via email.</DialogDescription>
        </DialogHeader>

        {mode === "search" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search for existing subs organizations</Label>
              <Input
                id="search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by organization name..."
                className="w-full"
              />
            </div>

            {loading && <div className="text-sm text-muted-foreground">Searching...</div>}

            {existingOrgs.length > 0 && (
              <div className="space-y-2">
                <Label>Found Organizations</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {existingOrgs.map((org) => (
                    <Card
                      key={org.id}
                      data-testid={`subcontractor-result-${org.id}`}
                      aria-disabled={org.linked}
                      className={org.linked ? "opacity-70" : "cursor-pointer hover:bg-muted/50"}
                      onClick={() => handleOrgSelect(org)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={org.logo ?? undefined} />
                            <AvatarFallback>{getInitials(org.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h4 className="font-medium">{org.name}</h4>
                            <p className="text-sm text-muted-foreground">{org.email}</p>
                            {org.linked && (
                              <Badge variant="secondary" className="mt-1">
                                Already added
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchQuery && existingOrgs.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">No existing organizations found.</p>
                <Button onClick={handleInviteNew} variant="outline">
                  Invite New Organization
                </Button>
              </div>
            )}

            {!searchQuery && (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">Start typing to search for organizations</p>
                <Button onClick={handleInviteNew} variant="outline">
                  Or invite a new organization
                </Button>
              </div>
            )}
          </div>
        )}

        {mode === "existing" && selectedOrg && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 border rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={selectedOrg.logo ?? undefined} />
                  <AvatarFallback>{getInitials(selectedOrg.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h4 className="font-medium">{selectedOrg.name}</h4>
                  <p className="text-sm text-muted-foreground">{selectedOrg.email}</p>
                </div>
              </div>

              <Alert>
                <AlertDescription>
                  This organization will be added to your subs network. You can assign services to them after adding.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMode("search")}>
                Back to Search
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Adding..." : "Add Organization"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {mode === "invite" && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Organization Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="organization@example.com"
                  required
                />
                <p className="text-sm text-muted-foreground">An invitation will be sent to this email address.</p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMode("search")}>
                Back to Search
              </Button>
              <Button type="submit" disabled={saving || !email.trim()}>
                {saving ? "Sending..." : "Send Invite"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
